import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AppBar, Avatar, Badge, IconButton, Stack, Toolbar, Typography, Link } from '@mui/material';
import { Menu, Notifications } from '@mui/icons-material';

import { AuthContext } from '@modules/Auth/contexts';
import { Authenticated } from '@modules/Auth/components';
import { UserContext } from '@modules/Users/contexts';
import { CompanyContext } from '@modules/Companies/contexts';
import { CompanyAutocomplete } from '@modules/Companies/components/CompanyAutocomplete';

import config from '@shared/config';
import { useRouter } from '@cvt/routing';

import { AltLogoText } from '@shared/components/Logo/AltLogoText';
import { UserMenu } from './UserMenu';
import { HeaderNav } from './HeaderNav';


type Props = {
  onNavigationClickRequest: () => void;
};

export const Header: React.FC<Props> = ({ onNavigationClickRequest }) => {
  const router = useRouter();
  const { user } = React.useContext(AuthContext);
  const { isAdmin } = React.useContext(UserContext);
  const { company = null, selectCompany } = React.useContext(CompanyContext);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 1px 2px 0px',
      }}
      elevation={0}
    >
      <Toolbar
        sx={{
          backgroundColor: 'background.default',
          color: 'primary.main',
        }}>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2, display: { xs: 'block', sm: 'none' } }}
          onClick={onNavigationClickRequest}
        >
          <Menu/>
        </IconButton>

        <Stack direction="row" flex={1} alignItems="center" justifyContent="flex-start">
          <Link
            component={RouterLink}
            to={router.home.path}
            underline="none"
          >
            <Typography variant="h1" component="div">
              <AltLogoText/>
            </Typography>
          </Link>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <HeaderNav/>
        </Stack>

        <Stack flex={1} direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          {isAdmin && (
            <CompanyAutocomplete
              sx={{ minWidth: 250 }}
              size="small"
              label="Customer"
              value={company}
              onChange={(e, value) => selectCompany(value)}
              TextFieldProps={{
                color: 'secondary',
              }}
            />
          )}
          {config.featureFlags.notifications && (
            <Authenticated>
              <IconButton
                size="large"
                color="inherit"
              >
                <Badge badgeContent={0} color="error">
                  <Notifications/>
                </Badge>
              </IconButton>
            </Authenticated>
          )}
          <UserMenu anchorEl={anchorElUser} onCloseRequest={handleCloseUserMenu}>
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} >
              <Avatar alt={user?.displayName} src={user?.profilePicture || undefined} sx={{ width: 28, height: 28 }}/>
            </IconButton>
          </UserMenu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
